import { useState } from "react"

import { useGetTestTemplateSuggestions } from "./useGetTestTemplateSuggestions"

import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import { type TestTemplate, TestTemplateListSearchBy } from "@/graphql/codegen/graphql"
import type { AutocompleteOption } from "@/types/form-fields"
import useDebounce from "@/utils/useDebounce"

interface UseAssociatedTestTemplateSelectDialogReturn {
  searchQuery: string | AutocompleteOption
  testTemplates: TestTemplate[]
  isFetching: boolean
  selectedTestTemplate: TestTemplate | null
  handleSearch: (searchedItem: string | AutocompleteOption) => void
  handleTestTemplateSelect: (testTemplateId: string | null) => void
}

interface UseAssociatedTestTemplateSelectDialogProps {
  productId: number
  companyId?: string | null
  locationId?: string | null
}

export const useAssociatedTestTemplateSelectDialog = ({
  productId,
  companyId,
  locationId,
}: UseAssociatedTestTemplateSelectDialogProps): UseAssociatedTestTemplateSelectDialogReturn => {
  const [searchQuery, setSearchQuery] = useState<string | AutocompleteOption>("")
  const [selectedTestTemplate, setSelectedTestTemplate] = useState<TestTemplate | null>(null)

  const handleSearch = (searchedItem: string | AutocompleteOption) => {
    setSearchQuery(searchedItem)
    setSelectedTestTemplate(null)
  }

  const handleTestTemplateSelect = (testTemplateId: string | null) => {
    setSelectedTestTemplate(testTemplates.find((template) => template.testTemplateId === testTemplateId) ?? null)
  }

  const debouncedSearchTerm = useDebounce(searchQuery, DEBOUNCE_DELAY_MS)
  const { testTemplates, fetching } = useGetTestTemplateSuggestions({
    query: typeof debouncedSearchTerm === "string" ? debouncedSearchTerm : (debouncedSearchTerm?.label ?? ""),
    searchBy: [TestTemplateListSearchBy.Description, TestTemplateListSearchBy.ExternalSystemId],
    productId,
    companyId,
    locationId,
  })

  return {
    searchQuery,
    testTemplates,
    isFetching: fetching,
    selectedTestTemplate,
    handleSearch,
    handleTestTemplateSelect,
  }
}
